// Datenmodell + Helfer für den Wartungs-Formular-Builder.
// Portiert aus frontend/wartungen/kategorie-einstellungen.js (normalizeMaintenanceField,
// createMaintenanceFieldFromTemplate, Layout-Normalisierung, Dirty-Payload).

import {
  LAYOUT_FIELD_TYPES,
  normalizeFieldType,
} from "@/lib/fieldTypeCatalog";
import type { BuilderSchemaField, FieldTemplate } from "@/lib/wartungen";
import { slugifyKey } from "@/pages/wartungen/lib/wartungenUi";

export type BuilderScope = "inspection" | "masterdata";

export interface FieldOption {
  label: string;
  value: string;
  active?: boolean;
  is_defect?: boolean;
  grade_effect?: number | null;
  articles?: ArticleRef[];
  sort_order?: number;
  [k: string]: unknown;
}

export interface ArticleRef {
  artikel_id?: number | string | null;
  hero_nr?: string | null;
  label?: string | null;
  qty?: number | null;
  [k: string]: unknown;
}

export interface VisibilityRule {
  rule_type: string; // "visibleIf" | "alwaysVisible"
  source_scope?: string;
  source_field_key?: string;
  operator?: string;
  value?: unknown;
}

export interface BuilderField {
  /** Stabiler Client-Key für React (field_key kann sich beim Umbenennen ändern). */
  uid: string;
  field_id: number | null;
  field_key: string;
  label: string;
  type: string;
  scope: BuilderScope;
  category: string;
  required: boolean;
  semantic_role: string;
  template_id: number | null;
  layout_row: number;
  layout_col: number;
  layout_span: number;
  layout_weight: number;
  options_override_json: Record<string, unknown>;
  ui_override_json: Record<string, unknown>;
  behavior_override_json: Record<string, unknown>;
}

let UID_COUNTER = 0;
export function nextUid(prefix = "f"): string {
  UID_COUNTER += 1;
  return `${prefix}_${Date.now().toString(36)}_${UID_COUNTER}`;
}

export function normalizeScope(raw: unknown): BuilderScope {
  return String(raw || "").trim().toLowerCase() === "masterdata" ? "masterdata" : "inspection";
}

export function normalizeCategory(raw: unknown): string {
  const v = String(raw ?? "").trim();
  return v;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

export function normalizeBehavior(value: unknown): Record<string, unknown> {
  const obj = asRecord(value);
  if (!Array.isArray(obj.rules)) obj.rules = [];
  return obj;
}

/** Schema-Feld → internes BuilderField. */
export function normalizeField(raw: BuilderSchemaField, _formId: string, _formName: string): BuilderField {
  const optionsOverride = asRecord(raw.options_override_json);
  const uiOverride = asRecord(raw.ui_override_json);
  const behaviorOverride = normalizeBehavior(raw.behavior_override_json);
  const type =
    normalizeFieldType(raw.type ?? raw.field_type ?? uiOverride.type) || "text";
  // Wichtig: Das Builder-Schema liefert den aufgelösten Scope als `effective_scope`
  // (scope_override ist NULL, wenn der Scope aus dem Template kommt).
  const scope = normalizeScope(raw.effective_scope ?? raw.scope_override ?? raw.scope);
  const semanticRoleOverride =
    scope === "masterdata"
      ? "info"
      : String(
          raw.semantic_role_override ?? raw.effective_semantic_role ?? raw.semantic_role ?? "",
        )
          .trim()
          .toLowerCase() || "info";

  const layoutCol = Math.max(0, Math.min(2, Number(raw.layout_col || 0)));
  const layoutSpanRaw = Math.max(1, Number(raw.layout_span || 3));
  const layoutSpan = Math.min(layoutSpanRaw, Math.max(1, 3 - layoutCol));

  return {
    uid: nextUid(),
    field_id: (raw.field_id ?? raw.id ?? null) as number | null,
    field_key: String(raw.field_key || raw.question_key || "").trim(),
    label: String(raw.label || raw.question_label || raw.field_key || "").trim(),
    type,
    scope,
    category: normalizeCategory(raw.category ?? optionsOverride.category),
    required: Boolean(
      LAYOUT_FIELD_TYPES.has(type)
        ? false
        : (raw.required_override ?? raw.required ?? optionsOverride.required),
    ),
    semantic_role: semanticRoleOverride,
    template_id: Number(raw.template_id || 0) || null,
    layout_row: Math.max(0, Number(raw.layout_row || 0)),
    layout_col: layoutCol,
    layout_span: layoutSpan,
    layout_weight: Math.max(1, Number(raw.layout_weight || layoutSpan)),
    options_override_json: optionsOverride,
    ui_override_json: uiOverride,
    behavior_override_json: behaviorOverride,
  };
}

/** Neues Feld aus einem Palette-Template (oder Builtin). */
export function createFieldFromTemplate(
  template: FieldTemplate | null,
  scope: BuilderScope,
  existingKeys: Set<string>,
): BuilderField {
  const fieldType = normalizeFieldType(template?.field_type) || "text";
  const templateKey = String(template?.template_key || fieldType || "feld");
  const defaultOptions = asRecord(template?.default_options_json);
  const baseKey = slugifyKey(templateKey) || "feld";
  let fieldKey = baseKey;
  let i = 2;
  while (existingKeys.has(fieldKey)) {
    fieldKey = `${baseKey}_${i}`;
    i += 1;
  }
  const isLayout = LAYOUT_FIELD_TYPES.has(fieldType);
  return {
    uid: nextUid(),
    field_id: null,
    field_key: fieldKey,
    label: String(template?.default_label || templateKey || fieldKey),
    type: fieldType,
    scope,
    category: normalizeCategory(template?.category_hint ?? defaultOptions.category),
    required: isLayout ? false : Boolean(defaultOptions.required),
    semantic_role:
      scope === "masterdata"
        ? "info"
        : String(template?.semantic_role || "info").trim().toLowerCase() || "info",
    template_id: Number(template?.id || 0) || null,
    layout_row: 0,
    layout_col: 0,
    layout_span: 3,
    layout_weight: 3,
    options_override_json: { ...defaultOptions },
    ui_override_json: { type: fieldType },
    behavior_override_json: { rules: [] },
  };
}

/* ── Layout-Helfer (3-Spalten-Raster je Scope) ───────────────────────────── */

export function fieldsForScope(fields: BuilderField[], scope: BuilderScope): BuilderField[] {
  return fields.filter((f) => f.scope === scope);
}

/** Felder eines Scopes nach layout_row/layout_col in Zeilen gruppieren. */
export function groupIntoRows(fields: BuilderField[]): BuilderField[][] {
  const sorted = [...fields].sort((a, b) => {
    if (a.layout_row !== b.layout_row) return a.layout_row - b.layout_row;
    return a.layout_col - b.layout_col;
  });
  const rows: BuilderField[][] = [];
  for (const f of sorted) {
    const row = rows[f.layout_row] || (rows[f.layout_row] = []);
    row.push(f);
  }
  return rows.filter((r) => r && r.length > 0);
}

/** Nach jeder strukturellen Änderung Reihen/Spalten je Scope normalisieren. */
export function normalizeRowLayoutForScope(fields: BuilderField[], scope: BuilderScope): void {
  const scoped = fieldsForScope(fields, scope);
  const rows = groupIntoRows(scoped);
  rows.forEach((row, rowIndex) => {
    const singleInRow = row.length === 1;
    const rowWeight = singleInRow ? 3 : 1;
    row.forEach((f, index) => {
      f.layout_row = rowIndex;
      f.layout_col = index;
      f.layout_span = singleInRow ? 3 : 1;
      f.layout_weight = rowWeight;
    });
  });
}

/** Feld als eigene Zeile an targetRow einfügen (andere Zeilen nach unten schieben). */
export function insertFieldAsSingleRow(
  fields: BuilderField[],
  field: BuilderField,
  scope: BuilderScope,
  targetRow: number,
): void {
  for (const f of fieldsForScope(fields, scope)) {
    if (f !== field && f.layout_row >= targetRow) f.layout_row += 1;
  }
  field.layout_row = targetRow;
  field.layout_col = 0;
  field.layout_span = 3;
  field.layout_weight = 3;
}

/**
 * Feld seitlich in eine bestehende Zeile einfügen (max. 3 Felder pro Zeile).
 * Portiert aus placeFieldIntoGridNearTarget in kategorie-einstellungen.js.
 */
export function placeFieldBesideAnchor(
  fields: BuilderField[],
  field: BuilderField,
  scope: BuilderScope,
  anchor: BuilderField,
  insertCol: number,
): void {
  const targetRow = Math.max(0, Number(anchor.layout_row) || 0);
  field.layout_span = 1;
  field.layout_weight = Math.max(1, Number(field.layout_weight) || 1);

  const originalRowFields = fieldsForScope(fields, scope)
    .filter((entry) => entry.layout_row === targetRow)
    .sort((a, b) => a.layout_col - b.layout_col);
  const sourceIndex = originalRowFields.findIndex((entry) => entry.uid === field.uid);

  let targetCol = Math.max(0, Number(insertCol) || 0);
  // Beim Umsortieren in derselben Zeile: Index nach Entfernen des gezogenen Felds korrigieren.
  if (sourceIndex >= 0 && sourceIndex < targetCol) {
    targetCol -= 1;
  }

  const rows = new Map<number, BuilderField[]>();
  const candidates = fieldsForScope(fields, scope)
    .filter((entry) => entry.uid !== field.uid)
    .sort((a, b) => {
      if (a.layout_row !== b.layout_row) return a.layout_row - b.layout_row;
      return a.layout_col - b.layout_col;
    });

  for (const entry of candidates) {
    const row = Math.max(0, Number(entry.layout_row) || 0);
    if (!rows.has(row)) rows.set(row, []);
    rows.get(row)!.push(entry);
  }
  if (!rows.has(targetRow)) rows.set(targetRow, []);
  const rowFields = rows.get(targetRow)!;
  const insertAt = Math.max(0, Math.min(targetCol, rowFields.length));
  rowFields.splice(insertAt, 0, field);

  let currentRow = targetRow;
  while (rows.has(currentRow)) {
    const list = rows.get(currentRow)!;
    if (list.length <= 3) {
      currentRow += 1;
      continue;
    }
    const overflow = list.splice(3);
    if (!rows.has(currentRow + 1)) rows.set(currentRow + 1, []);
    rows.set(currentRow + 1, [...overflow, ...rows.get(currentRow + 1)!]);
    currentRow += 1;
  }

  const orderedRows = Array.from(rows.keys()).sort((a, b) => a - b);
  for (const row of orderedRows) {
    const entries = rows.get(row) || [];
    const singleInRow = entries.length === 1;
    let cursorCol = 0;
    entries.forEach((entry) => {
      const desiredSpan = Math.max(1, Math.min(3, Number(entry.layout_span) || 1));
      const remaining = Math.max(1, 3 - cursorCol);
      const span = singleInRow ? 3 : Math.min(desiredSpan, remaining);
      const desiredWeight = Math.max(1, Number(entry.layout_weight) || span);
      entry.layout_row = row;
      entry.layout_col = singleInRow ? 0 : cursorCol;
      entry.layout_span = span;
      entry.layout_weight = singleInRow ? 3 : desiredWeight;
      cursorCol = Math.min(3, cursorCol + span);
    });
  }
}

export function buildExistingKeySet(fields: BuilderField[], exclude?: BuilderField): Set<string> {
  const set = new Set<string>();
  for (const f of fields) {
    if (f === exclude) continue;
    if (f.field_key) set.add(f.field_key);
  }
  return set;
}

/* ── Dirty-Payload (PUT /forms/{id}/builder/schema fields[]) ──────────────── */

export function fieldToSavePayload(
  field: BuilderField,
  formId: string,
  sortOrder: number,
): Record<string, unknown> {
  const normalizedType = field.type;
  const isLayout = LAYOUT_FIELD_TYPES.has(normalizedType);
  return {
    form_id: formId,
    field_key: field.field_key.trim(),
    label: (field.label || field.field_key || "").trim(),
    template_id: field.template_id ?? null,
    sort_order: sortOrder >= 0 ? sortOrder : null,
    layout_row: Number(field.layout_row || 0),
    layout_col: Number(field.layout_col || 0),
    layout_span: Number(field.layout_span || 1),
    layout_weight: Number(field.layout_weight || 1),
    required_override: isLayout ? false : Boolean(field.required),
    options_override_json: {
      ...(field.options_override_json || {}),
      category: field.category.trim() || null,
    },
    ui_override_json: {
      ...(field.ui_override_json || {}),
      type: normalizedType,
    },
    behavior_override_json: normalizeBehavior(field.behavior_override_json),
    semantic_role_override:
      field.scope === "masterdata" ? "info" : field.semantic_role || "info",
    scope_override: field.scope === "masterdata" ? "masterdata" : "inspection",
  };
}

/**
 * Semantische Sortier-Reihenfolge (alle Masterdata-Felder, dann Inspection),
 * jeweils nach Layout – für Validierung von Sichtbarkeitsregeln.
 */
export function semanticSortOrderByKey(fields: BuilderField[]): Map<string, number> {
  const order = new Map<string, number>();
  let idx = 0;
  for (const scope of ["masterdata", "inspection"] as BuilderScope[]) {
    const rows = groupIntoRows(fieldsForScope(fields, scope));
    for (const row of rows) {
      for (const f of row) {
        if (f.field_key) order.set(f.field_key, idx);
        idx += 1;
      }
    }
  }
  return order;
}
